import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import { alpha } from '@mui/material/styles';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';

/**
 * Compact room-type label for inventory/rate sticky columns.
 * Mobile: image left; code + count badge on one row (badge right-most).
 * Desktop/web: image + full name (+ optional badge on the right).
 */
export function resolveRoomTypeLabel(roomType, { isMobile = false } = {}) {
  const name = String(roomType?.name || '').trim();
  const code = String(roomType?.code || '').trim();
  if (isMobile) {
    if (code) return code;
    if (!name) return roomType?.id != null ? `RT${roomType.id}` : '—';
    const parts = name.replace(/-/g, ' ').split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
    return parts.slice(0, 3).map((p) => p[0]).join('').toUpperCase();
  }
  return name || code || (roomType?.id != null ? `Room Type ${roomType.id}` : 'Room Type');
}

export default function InventoryRoomTypeLabel({
  roomType,
  isMobile = false,
  isDark = true,
  title,
  fontWeight = 600,
  endAdornment = null,
  sx = {},
  textSx = {},
}) {
  const label = resolveRoomTypeLabel(roomType, { isMobile });
  const fullName = String(roomType?.name || label).trim();
  const imageUrl = roomType?.image_url || roomType?.room_type_image_url || null;
  const avatarSize = isMobile ? 22 : 28;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 0.4 : 0.75,
        flex: 1,
        minWidth: 0,
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        ...sx,
      }}
      title={title || fullName}
    >
      <Avatar
        src={imageUrl || undefined}
        variant="rounded"
        alt=""
        sx={{
          width: avatarSize,
          height: avatarSize,
          flexShrink: 0,
          bgcolor: alpha(DASHBOARD_ACCENT, isDark ? 0.18 : 0.22),
          color: DASHBOARD_ACCENT,
          border: `1px solid ${alpha(DASHBOARD_ACCENT, isDark ? 0.35 : 0.4)}`,
          fontSize: isMobile ? '0.65rem' : '0.75rem',
          '& img': { objectFit: 'cover' },
        }}
      >
        <MeetingRoomOutlinedIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
      </Avatar>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 0.25 : 0.75,
          minWidth: 0,
          flex: 1,
          width: '100%',
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight,
            fontSize: isMobile ? '0.58rem' : { xs: '0.7rem', sm: '0.8rem' },
            lineHeight: 1.15,
            letterSpacing: isMobile ? '0.02em' : 0,
            textAlign: 'left',
            minWidth: 0,
            flex: '1 1 auto',
            pr: 0.25,
            ...textSx,
          }}
        >
          {label}
        </Typography>
        {endAdornment ? (
          <Box sx={{ flexShrink: 0, ml: 'auto' }}>
            {endAdornment}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
